import { Component } from '@theme/component';

/**
 * Copies a discount code to the clipboard and flashes a confirmation.
 *
 * The theme's own 'copy-to-clipboard.js' reveals a success message and leaves it
 * revealed. The launch-offer chip is a persistent element a shopper may click
 * more than once, so this swaps the label and swaps it back — otherwise the
 * second click looks like nothing happened.
 *
 * Both labels are server-rendered so the strings stay translated, and both stay
 * in the layout so the chip keeps one width — this only flips 'data-copied',
 * which the stylesheet turns into a visibility swap.
 *
 * @typedef {Object} CopyCodeRefs
 * @property {HTMLElement} done - The "copied" label, also the announcement text
 * @property {HTMLElement} [status] - Visually hidden live region
 *
 * @extends {Component<CopyCodeRefs>}
 */
class DavittaCopyCode extends Component {
  requiredRefs = ['done'];

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#timer);
  }

  async copy() {
    const code = this.dataset.code;

    if (!code || !navigator.clipboard) return;

    try {
      await navigator.clipboard.writeText(code);
    } catch (error) {
      // A denied clipboard permission is not worth a visible failure: the code
      // is printed on the chip, so the shopper can still read and type it.
      console.warn('[davitta-copy-code] Could not write to the clipboard:', error);
      return;
    }

    this.#confirm();
  }

  #confirm() {
    const { done, status } = this.refs;

    this.dataset.copied = 'true';

    // Assigning the text on demand rather than server-rendering it into the live
    // region keeps the announcement tied to the click instead of to page load.
    if (status) status.textContent = done.textContent?.trim() ?? '';

    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => {
      this.dataset.copied = 'false';
      if (status) status.textContent = '';
    }, 2000);
  }
}

// The '-component' suffix is not cosmetic: component.js resolves 'on:click'
// against the closest element whose tag ends in it, and only such an element
// gets the upgrade-race retry. Without the suffix a click that lands before this
// module executes is dropped silently.
if (!customElements.get('davitta-copy-code-component')) {
  customElements.define('davitta-copy-code-component', DavittaCopyCode);
}
